import { gql } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import { useEffect, useState } from "react";
import styled, { css } from "styled-components";

import ReadCategory from "./ReadCategory.graphql";
import UpsertCategory from "./UpsertCategory.graphql";
import * as UI from "../../../../../modules/ui-kit";
import Modal from "../../../components/Modal";
import CategoryTree from "../CategoryInventory/CategoryTree";
import ListCategories from "../CategoryInventory/ListCategories.graphql";
import tree from "../CategoryInventory/tree";
import categoryDefinition from "../categoryDefinition";

const StyledParentCategory = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;

    .actions {
        align-items: center;
        display: flex;
        flex-direction: row;
        gap: 12px;
    }
`;

const StyledModalContent = styled.div`
    display: flex;
    flex-direction: column;
    height: 50vh;

    & > ul:first-of-type {
        flex: auto;
        overflow-y: auto;
    }

    & > div:last-of-type {
        flex: none;
        padding: 10px;

        button {
            margin: 5px;
            padding: 5px 10px;
        }
    }

    h3 {
        padding-left: 20px;
    }
`;

const CssSelected = css`
    color: royalblue;
    font-weight: bold;
    padding: 5px 0;
`;

const StyledLeaf = styled.span<{ $selected?: boolean }>`
    cursor: pointer;
    display: block;

    &:hover {
        color: darkslateblue;
    }

    ${ ({ $selected }) => $selected && CssSelected }
`;

export default function ParentCategory(props) {
    const { id } = props;

    const readCategory = useQuery(
        gql(ReadCategory),
        {
            variables: {
                id,
            },
        },
    );

    const listCategories = useQuery(gql(ListCategories));

    const [upsertCategory] = useMutation(
        gql(UpsertCategory),
        {
            // Reparenting moves the category in the tree, and the other editors
            // on this screen build their payload from ReadCategory.
            awaitRefetchQueries: true,
            refetchQueries: [
                gql(ReadCategory),
                "ListCategories",
            ],
            variables: {
                id,
            },
        },
    );

    const [active, setActive] = useState(false);
    const [busy, setBusy] = useState(false);
    const [selected, setSelected] = useState(undefined);

    useEffect(
        () => {
            setSelected(readCategory.data?.additionalCategoryData?.parentCategory?.id);
        },
        [readCategory.data?.additionalCategoryData?.parentCategory?.id],
    );

    if (readCategory.loading || listCategories.loading) return "loading";
    if (readCategory.error || listCategories.error) return "error";

    const parentCategory = readCategory.data.additionalCategoryData.parentCategory;

    async function assign() {
        if (!selected) return;
        if (selected === parentCategory?.id) return;
        setBusy(true);
        try {
            await upsertCategory(
                {
                    variables: {
                        definition: categoryDefinition(
                            readCategory.data,
                            {
                                parentCategory: { id: selected },
                            },
                        ),
                    },
                }
            );
            setActive(false);
        } finally {
            setBusy(false);
        }
    }

    async function setAsTopLevel() {
        setBusy(true);
        try {
            await upsertCategory(
                {
                    variables: {
                        definition: categoryDefinition(
                            readCategory.data,
                            {
                                parentCategory: null,
                            },
                        ),
                    },
                }
            );
            setActive(false);
        } finally {
            setBusy(false);
        }
    }

    return (
        <StyledParentCategory>
            <UI.FieldDescription>
                {
                    parentCategory
                        ? "Subcategory of:"
                        : "Top level category — shown directly in the app menu, with an icon."
                }
            </UI.FieldDescription>
            <div className="actions">
                { parentCategory && <strong>{ parentCategory.name[0].text }</strong> }
                <UI.Button
                    disabled={ busy }
                    onClick={ () => setActive(true) }
                >
                    { parentCategory ? "Change parent" : "Assign parent" }
                </UI.Button>
            </div>
            <Modal
                isOpen={ active }
                onClose={ () => setActive(false) }
            >
                <StyledModalContent>
                    <h3>assign parent Category</h3>
                    <CategoryTree
                        render={
                            (category) => (
                                <StyledLeaf
                                    $selected={ selected === category.id }
                                    onClick={ () => setSelected(category.id) }
                                >
                                    { category.name[0].text }
                                </StyledLeaf>
                            )
                        }
                    >
                        { tree(listCategories.data.categories) }
                    </CategoryTree>
                    <div>
                        <UI.Button
                            disabled={ busy || !parentCategory }
                            onClick={ setAsTopLevel }
                        >
                            Set as top level
                        </UI.Button>
                        <UI.Button
                            $appearance="primary"
                            disabled={ busy || !selected || selected === parentCategory?.id }
                            onClick={ assign }
                        >
                            { busy ? "Saving…" : "Assign" }
                        </UI.Button>
                        <UI.Button
                            $appearance="subtle"
                            disabled={ busy }
                            onClick={ () => setActive(false) }
                        >
                            Cancel
                        </UI.Button>
                    </div>
                </StyledModalContent>
            </Modal>
        </StyledParentCategory>
    );
}
